import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  List,
  ListItem,
  ListItemText,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  Save as SaveIcon,
  ColorLens as ColorIcon
} from '@mui/icons-material';

// 색상 선택 컴포넌트
const ColorPicker = ({ value, onChange }) => {
  const colors = [
    { color: 'rgba(40, 167, 69, 0.3)', label: '초록' },
    { color: 'rgba(255, 193, 7, 0.3)', label: '노랑' },
    { color: 'rgba(220, 53, 69, 0.3)', label: '빨강' },
    { color: 'rgba(23, 162, 184, 0.3)', label: '파랑' },
    { color: 'rgba(108, 117, 125, 0.3)', label: '회색' },
    { color: '', label: '없음' }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        색상 선택
      </Typography>
      <Grid container spacing={1}>
        {colors.map((item, index) => (
          <Grid item key={index}>
            <Box
              onClick={() => onChange(item.color)}
              sx={{
                width: 40,
                height: 40,
                cursor: 'pointer',
                backgroundColor: item.color || '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: value === item.color ? '0 0 0 2px #000' : 'none',
              }}
            >
              {item.color === '' && <Typography variant="body2">X</Typography>}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const Statuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('add');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: 'rgba(40, 167, 69, 0.3)'
  });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const navigate = useNavigate();

  // 상태 목록 불러오기
  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/statuses');
      setStatuses(res.data);
      setError(null);
      setHasOrderChanged(false);
    } catch (err) {
      setError(err.response?.data?.message || '상태 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 폼 데이터 초기화
  const resetFormData = () => {
    setFormData({
      name: '',
      color: 'rgba(40, 167, 69, 0.3)'
    });
  };

  // 다이얼로그 열기
  const handleOpenDialog = (action, status = null) => {
    setDialogAction(action);
    
    if (action === 'edit' && status) {
      setSelectedStatus(status);
      setFormData({
        name: status.name || '',
        color: status.color || ''
      });
    } else {
      resetFormData();
    }
    
    setOpenDialog(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetFormData();
  };

  // 상태 추가 또는 수정 폼 제출
  const handleSubmit = async () => {
    try {
      if (dialogAction === 'add') {
        // 상태 추가
        await api.post('/statuses', formData);
      } else {
        // 상태 수정
        await api.put(`/statuses/${selectedStatus.id}`, formData);
      }
      
      // 다이얼로그 닫고 목록 새로고침
      handleCloseDialog();
      fetchStatuses();
    } catch (err) {
      setError(err.response?.data?.message || '저장하는 중 오류가 발생했습니다.');
    }
  };

  // 폼 입력 변경
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // 색상 변경
  const handleColorChange = (color) => {
    setFormData({
      ...formData,
      color
    });
  };

  // 삭제 확인 다이얼로그 열기
  const handleDeleteClick = (status) => {
    setStatusToDelete(status);
    setDeleteConfirm(true);
  };

  // 상태 삭제
  const handleDelete = async () => {
    try {
      await api.delete(`/statuses/${statusToDelete.id}`);
      setDeleteConfirm(false);
      fetchStatuses();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '삭제하는 중 오류가 발생했습니다.');
    }
  };

  // 드래그 종료 시 호출될 함수
  const handleDragEnd = (result) => {
    // 드래그가 취소된 경우
    if (!result.destination) {
      return;
    }

    // 순서가 변경되지 않은 경우
    if (result.destination.index === result.source.index) {
      return;
    }

    // 상태 목록 순서 업데이트
    const reorderedStatuses = Array.from(statuses);
    const [removed] = reorderedStatuses.splice(result.source.index, 1);
    reorderedStatuses.splice(result.destination.index, 0, removed);

    setStatuses(reorderedStatuses);
    setHasOrderChanged(true);
  };

  // 변경된 순서 저장
  const saveOrder = async () => {
    try {
      setLoading(true);
      // 서버에 순서 정보만 전송
      const orderedStatuses = statuses.map((status, index) => ({
        id: status.id,
        order: index + 1
      }));
      
      await api.put('/statuses/order/update', { statuses: orderedStatuses });
      setHasOrderChanged(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || '순서 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          상태 관리
        </Typography>
        <Box>
          {hasOrderChanged && (
            <Tooltip title="변경된 순서 저장">
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={saveOrder}
                sx={{ mr: 1 }}
              >
                순서 저장
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            상태 추가
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 0, width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="statuses">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {statuses.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="등록된 상태가 없습니다." />
                    </ListItem>
                  ) : (
                    statuses.map((status, index) => (
                      <Draggable 
                        key={status.id.toString()} 
                        draggableId={status.id.toString()} 
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              marginBottom: '8px',
                            }}
                          >
                            <Paper 
                              sx={{ 
                                mb: 1,
                                bgcolor: snapshot.isDragging ? '#f5f5f5' : 'white',
                                overflow: 'hidden'
                              }}
                              elevation={snapshot.isDragging ? 3 : 1}
                            >
                              <ListItem
                                secondaryAction={
                                  <Box>
                                    <Tooltip title="수정">
                                      <IconButton
                                        edge="end"
                                        aria-label="edit"
                                        onClick={() => handleOpenDialog('edit', status)}
                                        sx={{ mr: 1 }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="삭제">
                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => handleDeleteClick(status)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                }
                              >
                                <Box {...provided.dragHandleProps} sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                                  <DragIndicatorIcon color="action" />
                                </Box>
                                <Box 
                                  sx={{ 
                                    width: 24, 
                                    height: 24, 
                                    borderRadius: '4px', 
                                    backgroundColor: status.color || 'transparent',
                                    border: status.color ? 'none' : '1px dashed #ccc',
                                    mr: 2
                                  }} 
                                />
                                <ListItemText primary={status.name} />
                              </ListItem>
                            </Paper>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>

      {/* 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction === 'add' ? '상태 추가' : '상태 수정'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            name="name"
            label="상태 이름"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
          />
          <ColorPicker value={formData.color} onChange={handleColorChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogAction === 'add' ? '추가' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>상태 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography>
            '{statusToDelete?.name}' 상태를 정말로 삭제하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>취소</Button>
          <Button onClick={handleDelete} color="error">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Statuses; 